import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    Box,
    Chip,
    Grid,
    IconButton,
    Link,
    Stack,
    Typography
} from '@mui/material';
import { DeleteOutline, ThumbUp } from '@mui/icons-material';

import { Block, SectionDivider } from '@commonComponents';
import {
    addTenantRecommendation,
    getTenantRecommendations,
    removeTenantRecommendation,
    searchTenants,
    RecommendationDirection,
    TenantRecommendationParty
} from '@api-admin/tenants';
import toast from '@utils/toast';
import { AutocompleteField } from '../CommonComponents/Form';

type TenantSearchOption = {
    id: number,
    name: string,
    orgNumber?: string
};

type PropsType = {
    tenantId: number,
    editable?: boolean
};

function PartyName({ party }: { party: TenantRecommendationParty }) {
    return (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
            <Box sx={{ minWidth: 0 }}>
                <Link
                    component={RouterLink}
                    to={`/tenants/${party.id}`}
                    underline="hover"
                    sx={{ fontWeight: 500 }}
                >
                    {party.name}
                </Link>
                {party.orgNumber && (
                    <Typography variant="body2" color="text.secondary" component="span" sx={{ ml: 1 }}>
                        {party.orgNumber}
                    </Typography>
                )}
            </Box>
            {!party.isVerified && (
                <Chip label="Ej verifierad" size="small" variant="outlined" />
            )}
        </Stack>
    );
}

function PartyList({ parties }: { parties: TenantRecommendationParty[] }) {
    if (parties.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary" sx={{ px: 2, mt: 1 }}>
                Inga rekommendationer
            </Typography>
        );
    }

    return (
        <Box component="ul" sx={{ px: 2, mt: 1, mb: 0 }}>
            {parties.map((party) => (
                <Box component="li" key={party.id} sx={{ py: 0.25 }}>
                    <PartyName party={party} />
                </Box>
            ))}
        </Box>
    );
}

function EditorColumn({
    title,
    direction,
    tenantId,
    parties,
    excludeIds,
    autocompleteKey,
    deletingId,
    onAdd,
    onRemove
}: {
    title: string,
    direction: RecommendationDirection,
    tenantId: number,
    parties: TenantRecommendationParty[],
    excludeIds: Set<number>,
    autocompleteKey: number,
    deletingId: number | null,
    onAdd: (option: TenantSearchOption) => void,
    onRemove: (party: TenantRecommendationParty) => void
}) {
    const excludeIdsRef = React.useRef(excludeIds);
    excludeIdsRef.current = excludeIds;

    const fetchOptions = React.useCallback(async (query?: string) => {
        if (!query || query.trim().length < 2) {
            return [];
        }
        const results = await searchTenants(query.trim()) as TenantSearchOption[];
        return (results || [])
            .filter((t) => !excludeIdsRef.current.has(t.id))
            .slice(0, 10);
    }, []);

    return (
        <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {title}
                {' '}
                ({parties.length})
            </Typography>
            <AutocompleteField<TenantSearchOption>
                key={`${direction}-${autocompleteKey}`}
                label="Sök företag"
                optionIdKey="id"
                fetchOptions={fetchOptions}
                minQueryLength={2}
                maxOptions={10}
                getOptionName={(option) => option.name}
                getOptionDescription={(option) => (
                    option.orgNumber ? `Org.nr ${option.orgNumber}` : ''
                )}
                displayOption={null}
                onChange={(option: TenantSearchOption | null) => {
                    if (option?.id && option.id !== tenantId) {
                        onAdd(option);
                    }
                }}
                filterOptions={(opts) => opts}
                variant="outlined"
                size="small"
            />
            <Box sx={{ mt: 1 }}>
                {parties.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                        Inga rekommendationer
                    </Typography>
                ) : parties.map((party) => (
                    <Stack
                        key={party.id}
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        sx={{ py: 0.5 }}
                    >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <PartyName party={party} />
                        </Box>
                        <IconButton
                            aria-label="Ta bort rekommendation"
                            color="error"
                            size="small"
                            disabled={deletingId === party.id}
                            onClick={() => onRemove(party)}
                        >
                            <DeleteOutline fontSize="small" />
                        </IconButton>
                    </Stack>
                ))}
            </Box>
        </Grid>
    );
}

export default function TenantRecommendations({ tenantId, editable = false }: PropsType) {
    const [received, setReceived] = React.useState<TenantRecommendationParty[]>([]);
    const [given, setGiven] = React.useState<TenantRecommendationParty[]>([]);
    const [autocompleteKey, setAutocompleteKey] = React.useState(0);
    const [deleting, setDeleting] = React.useState<{ id: number, direction: RecommendationDirection } | null>(null);

    const load = React.useCallback(async () => {
        const data = await getTenantRecommendations(tenantId);
        setReceived(data.received || []);
        setGiven(data.given || []);
    }, [tenantId]);

    React.useEffect(() => {
        load().catch((e) => {
            toast(e, 'Kunde inte hämta rekommendationer');
        });
    }, [load]);

    const add = React.useCallback(async (
        option: TenantSearchOption,
        direction: RecommendationDirection
    ) => {
        try {
            await addTenantRecommendation(tenantId, {
                otherTenantId: option.id,
                direction
            });
            toast(`Rekommendation tillagd: ${option.name}`, undefined, true);
            setAutocompleteKey((k) => k + 1);
            await load();
        } catch (e) {
            toast(e, 'Kunde inte lägga till rekommendationen');
            setAutocompleteKey((k) => k + 1);
        }
    }, [load, tenantId]);

    const remove = React.useCallback(async (
        party: TenantRecommendationParty,
        direction: RecommendationDirection
    ) => {
        setDeleting({ id: party.id, direction });
        try {
            await removeTenantRecommendation(tenantId, party.id, direction);
            toast(`Rekommendation borttagen: ${party.name}`, undefined, true);
            await load();
        } catch (e) {
            toast(e, 'Kunde inte ta bort rekommendationen');
        }
        setDeleting(null);
    }, [load, tenantId]);

    const receivedExclude = React.useMemo(
        () => new Set([tenantId, ...received.map((p) => p.id)]),
        [received, tenantId]
    );
    const givenExclude = React.useMemo(
        () => new Set([tenantId, ...given.map((p) => p.id)]),
        [given, tenantId]
    );

    if (!editable) {
        return (
            <Block
                title="Rekommendationer"
                variant="outlined"
                IconComponent={ThumbUp}
            >
                <Grid container spacing={0} sx={{ mt: 1, px: 2 }}>
                    <Grid item xs={12} xl={6} px={2} pb={2}>
                        <SectionDivider
                            label={`Rekommenderad av (${received.length})`}
                            marginTop={0}
                            marginBottom={0}
                        />
                        <PartyList parties={received} />
                    </Grid>
                    <Grid item xs={12} xl={6} px={2} pb={2}>
                        <SectionDivider
                            label={`Rekommenderar (${given.length})`}
                            marginTop={0}
                            marginBottom={0}
                        />
                        <PartyList parties={given} />
                    </Grid>
                </Grid>
            </Block>
        );
    }

    return (
        <Grid item xs={12}>
            <SectionDivider
                label="Rekommendationer"
                icon={<ThumbUp fontSize="small" />}
                marginTop={2}
                marginBottom={0}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Endast rekommendationer från verifierade företag visas på den publika profilen,
                och bara om det rekommenderade företaget självt är verifierat.
            </Typography>
            <Grid container spacing={3}>
                <EditorColumn
                    title="Rekommenderad av"
                    direction="received"
                    tenantId={tenantId}
                    parties={received}
                    excludeIds={receivedExclude}
                    autocompleteKey={autocompleteKey}
                    deletingId={deleting?.direction === 'received' ? deleting.id : null}
                    onAdd={(option) => add(option, 'received')}
                    onRemove={(party) => remove(party, 'received')}
                />
                <EditorColumn
                    title="Rekommenderar"
                    direction="given"
                    tenantId={tenantId}
                    parties={given}
                    excludeIds={givenExclude}
                    autocompleteKey={autocompleteKey}
                    deletingId={deleting?.direction === 'given' ? deleting.id : null}
                    onAdd={(option) => add(option, 'given')}
                    onRemove={(party) => remove(party, 'given')}
                />
            </Grid>
        </Grid>
    );
}
